import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { copyProject, createProject, removeProject, restoreProject } from "features/creator/api";
import { fetchAccountPlan } from "./api";
import { DEFAULT_ACCOUNT_LIMITS, DEFAULT_PLAN } from "./constants";
import {
  AccountPlanResponse,
  LoadingStatus,
  SubscriptionState,
  NativePackagesStatus,
  NativePackage,
} from "./types";

const applyProjectsDelta = (state: SubscriptionState, uid: string, delta: number) => {
  const limits = state.playerLimits[uid];
  if (!limits) return;
  limits.projects.used = Math.max(0, limits.projects.used + delta);
  if (!limits.projects.unlimited) {
    limits.projects.remaining = Math.max(0, limits.projects.limit - limits.projects.used);
  }
};

const initialState: SubscriptionState = {
  plan: DEFAULT_PLAN,
  accountLimits: DEFAULT_ACCOUNT_LIMITS,
  playerLimits: {},
  entitlement: null,
  status: LoadingStatus.IDLE,
  nativePackages: {
    packages: [],
    status: NativePackagesStatus.IDLE,
  },
  purchaseRequestByChild: {},
};

const subscriptionSlice = createSlice({
  name: "subscription",
  initialState,
  reducers: {
    /**
     * AIチャット使用量を更新（子供単位）
     */
    updateAiChatUsage(
      state,
      action: PayloadAction<{ uid: string; used: number; remaining: number }>,
    ) {
      const { uid, used, remaining } = action.payload;
      if (state.playerLimits[uid]) {
        state.playerLimits[uid].aiChatDaily.used = used;
        state.playerLimits[uid].aiChatDaily.remaining = remaining;
      }
    },
    /**
     * AI画像生成使用量を更新（子供単位）
     */
    updateAiImageUsage(
      state,
      action: PayloadAction<{ uid: string; used: number; remaining: number }>,
    ) {
      const { uid, used, remaining } = action.payload;
      if (state.playerLimits[uid]) {
        state.playerLimits[uid].aiImageDaily.used = used;
        state.playerLimits[uid].aiImageDaily.remaining = remaining;
      }
    },
    /**
     * ネイティブパッケージ取得開始
     */
    setNativePackagesLoading(state) {
      state.nativePackages.status = NativePackagesStatus.LOADING;
    },
    /**
     * ネイティブパッケージ取得成功
     */
    setNativePackages(state, action: PayloadAction<NativePackage[]>) {
      state.nativePackages.packages = action.payload;
      state.nativePackages.status = NativePackagesStatus.SUCCEEDED;
    },
    /**
     * ネイティブパッケージ取得失敗
     */
    setNativePackagesFailed(state) {
      state.nativePackages.status = NativePackagesStatus.FAILED;
    },
    /**
     * ネイティブパッケージ未サポート（Amazon等）
     */
    setNativePackagesNotSupported(state) {
      state.nativePackages.status = NativePackagesStatus.NOT_SUPPORTED;
    },
    /**
     * Asset使用量を更新（アカウント + プレイヤー両方）
     */
    updateAssetUsage(state, action: PayloadAction<{ uid: string; delta: number }>) {
      const { uid, delta } = action.payload;

      // 1. accountLimits.assets を更新
      state.accountLimits.assets.used += delta;
      if (!state.accountLimits.assets.unlimited) {
        state.accountLimits.assets.remaining = Math.max(
          0,
          state.accountLimits.assets.limit - state.accountLimits.assets.used,
        );
      }

      // 2. playerLimits[uid].assets を更新
      if (state.playerLimits[uid]) {
        state.playerLimits[uid].assets.used += delta;
        if (!state.playerLimits[uid].assets.unlimited) {
          state.playerLimits[uid].assets.remaining = Math.max(
            0,
            state.playerLimits[uid].assets.limit - state.playerLimits[uid].assets.used,
          );
        }
      }
    },
    /**
     * 保護者への課金お願い送信を記録（クールダウン・送信済み表示用）
     */
    markPurchaseRequested(state, action: PayloadAction<{ childUid: string; requestedAt: string }>) {
      const { childUid, requestedAt } = action.payload;
      state.purchaseRequestByChild[childUid] = { requestedAt };
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchAccountPlan
      .addCase(fetchAccountPlan.pending, (state) => {
        state.status = LoadingStatus.LOADING;
      })
      .addCase(fetchAccountPlan.fulfilled, (state, action: PayloadAction<AccountPlanResponse>) => {
        state.status = LoadingStatus.SUCCEEDED;
        state.plan = action.payload.plan;
        state.entitlement = action.payload.entitlement;
        state.accountLimits.players = action.payload.players;
        state.accountLimits.assets = action.payload.assets;
        // APIからchatHistoryLimitが返された場合のみ更新（usedのみを反映）
        if (action.payload.chatHistoryLimit) {
          state.accountLimits.chatHistoryLimit = {
            ...state.accountLimits.chatHistoryLimit,
            used: action.payload.chatHistoryLimit.used,
          };
        }
        state.playerLimits = action.payload.playerLimits;
      })
      .addCase(fetchAccountPlan.rejected, (state) => {
        state.status = LoadingStatus.FAILED;
      })
      .addCase(createProject.fulfilled, (state, action) => {
        applyProjectsDelta(state, action.meta.arg.uid, +1);
      })
      .addCase(copyProject.fulfilled, (state, action) => {
        applyProjectsDelta(state, action.meta.arg.uid, +1);
      })
      .addCase(removeProject.fulfilled, (state, action) => {
        applyProjectsDelta(state, action.meta.arg.uid, -1);
      })
      .addCase(restoreProject.fulfilled, (state, action) => {
        applyProjectsDelta(state, action.meta.arg.uid, +1);
      });
  },
});

export const {
  updateAiChatUsage,
  updateAiImageUsage,
  setNativePackagesLoading,
  setNativePackages,
  setNativePackagesFailed,
  setNativePackagesNotSupported,
  updateAssetUsage,
  markPurchaseRequested,
} = subscriptionSlice.actions;
export default subscriptionSlice.reducer;
